import { useState, type FormEvent } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { getOnboardingProfile, updateOnboardingProfile, type EditableProfile } from "@/api/onboarding";
import { DEFAULT_ONBOARDING, type OnboardingData } from "@/stores/onboardingStore";
import { Card, SectionLabel } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { ProfileFields } from "@/components/profile/ProfileFields";

export function ProfileEditor() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: ["onboarding-profile"], queryFn: getOnboardingProfile });
  const [draft, setDraft] = useState<OnboardingData | null>(null);
  const [baseline, setBaseline] = useState<OnboardingData | null>(null);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  async function save(event: FormEvent) {
    event.preventDefault();
    if (!draft || !baseline) return;
    setBusy(true); setError(null); setMessage(null);
    const patch: Partial<EditableProfile> = {};
    for (const key of Object.keys(DEFAULT_ONBOARDING) as (keyof OnboardingData)[]) {
      if (key !== "informed_consent_signed" && JSON.stringify(draft[key]) !== JSON.stringify(baseline[key])) Object.assign(patch, { [key]: draft[key] });
    }
    try {
      const saved = await updateOnboardingProfile(patch);
      queryClient.setQueryData(["onboarding-profile"], saved);
      await queryClient.invalidateQueries({ predicate: query => query.queryKey[0] !== "onboarding-profile" });
      setDraft(null); setBaseline(null); setMessage(t("profile_editor.saved"));
    } catch {
      setError(t("profile_editor.save_error"));
    } finally { setBusy(false); }
  }
  return <Card>
    <SectionLabel>{t("profile_editor.title")}</SectionLabel>
    {query.isError ? <div role="alert"><p>{t("profile_editor.load_error")}</p><Button onClick={() => query.refetch()}>{t("common.retry")}</Button></div> : null}
    {!draft ? <div className="mt-3 space-y-3">
      <p className="text-[13px] text-taupe">{t("profile_editor.description")}</p>
      <Button disabled={query.isPending || query.isError} onClick={() => { const current = { ...DEFAULT_ONBOARDING, ...query.data }; setBaseline(current); setDraft(current); setMessage(null); }}>{t("profile_editor.edit")}</Button>
      {message ? <p role="status" className="text-[13px]">{message}</p> : null}
    </div> : <form className="mt-4 space-y-5" onSubmit={save}>
      <fieldset disabled={busy} className="space-y-5">
        <Input label={t("onboarding.step.welcome.first_name_label")} value={draft.first_name ?? ""} maxLength={100} onChange={event => setDraft({ ...draft, first_name: event.target.value || null })} />
        <Input type="date" label={t("onboarding.step.welcome.dob_label")} max={new Date().toISOString().slice(0, 10)} value={draft.date_of_birth ?? ""} onChange={event => setDraft({ ...draft, date_of_birth: event.target.value || null })} />
        <label className="block text-[13px]">{t("onboarding.step.welcome.sex_label")}<select className="ml-3 rounded-lg border border-cream-border p-2" value={draft.sex_biological} onChange={event => setDraft({ ...draft, sex_biological: event.target.value as OnboardingData["sex_biological"] })}>{["prefer_not_to_say", "male", "female"].map(value => <option key={value} value={value}>{t(`onboarding.sex.${value}`)}</option>)}</select></label>
        {(["body", "schedule", "nutrition", "context"] as const).map(section => <ProfileFields key={section} section={section} data={draft} onChange={patch => setDraft(current => current ? { ...current, ...patch } : current)} />)}
      </fieldset>
      {error ? <p role="alert" className="text-[13px] text-coral">{error}</p> : null}
      <div className="flex gap-3"><Button type="submit" loading={busy}>{t("profile_editor.save")}</Button><Button type="button" variant="secondary" disabled={busy} onClick={() => { setDraft(null); setError(null); }}>{t("common.cancel")}</Button></div>
    </form>}
  </Card>;
}
